import React, { useEffect, useState } from 'react';
import { revolutApi, bankTransferApi, paymentMethodsApi, PaymentMethod, BankTransferDetails } from '../services/api';
import { openRevolutCheckout } from '../utils/revolutCheckout';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';
import { collectBrowserData } from '../utils/browserData';

function shortBrand(raw: string | null | undefined): string {
  const base = (raw || '').toUpperCase().replace(/_(DEBIT|CREDIT|PREPAID)$/i, '');
  const map: Record<string, string> = {
    MASTERCARD: 'MC', AMERICAN_EXPRESS: 'AMEX', AMEX: 'AMEX',
    DISCOVER: 'DISC', VISA: 'VISA', JCB: 'JCB', UNIONPAY: 'UPI',
    DINERS_CLUB: 'DINERS', MAESTRO: 'MAESTRO',
  };
  return map[base] || base || 'CARD';
}
function readableBrand(raw: string | null | undefined): string {
  return (raw || '').toUpperCase().replace(/_(DEBIT|CREDIT|PREPAID)$/i, '');
}

interface Props {
  advertiserId: string;
  currency: string;
  onClose: () => void;
  onCheckoutOpened?: () => void;
  /** Pre-check the "save card" toggle — used when the dialog is opened
   *  from the Payment Methods tab where saving is the goal. */
  prefillSaveCard?: boolean;
  /** True when the office's payment profile has card_provider configured. */
  cardAvailable?: boolean;
  /** True when the office's payment profile has bank_provider configured. */
  bankAvailable?: boolean;
}

// Quick top-up presets are currency-aware. A KZT unit is ~1/500 of a EUR one,
// so 50/100/200/500 are uselessly small for KZT balances. A dirham is ~1/4.
const PRESETS_BY_CURRENCY: Record<string, number[]> = {
  KZT: [100000, 200000, 500000],
  AED: [200, 500, 1000, 2000],
};
const presetAmountsFor = (currency?: string): number[] =>
  PRESETS_BY_CURRENCY[(currency || '').toUpperCase()] || [50, 100, 200, 500];

type Method = 'card' | 'bank';

const TopUpDialog: React.FC<Props> = ({
  advertiserId, currency, onClose, onCheckoutOpened, prefillSaveCard,
  cardAvailable = true, bankAvailable = true,
}) => {
  const { t } = useTranslation();
  // Default to whichever rail is actually available
  const [method, setMethod] = useState<Method>(cardAvailable ? 'card' : 'bank');
  const presetAmounts = presetAmountsFor(currency);
  const [amount, setAmount] = useState<string>(() => String(presetAmounts[0]));
  const [saveCard, setSaveCard] = useState(!!prefillSaveCard);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankDetails, setBankDetails] = useState<BankTransferDetails | null>(null);
  const [savedCards, setSavedCards] = useState<PaymentMethod[] | null>(null);
  // null = "use a new card", number = saved card id
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // FX preview: when the office's payment profile charges in a different
  // currency than the balance, show "≈ X EUR will be charged to your card".
  const [fxPreview, setFxPreview] = useState<{
    charge_amount: number;
    charge_currency: string;
    fx_rate: number;
    fx_margin_pct: number;
  } | null>(null);
  const [fxPreviewError, setFxPreviewError] = useState<string | null>(null);
  const [fxPreviewLoading, setFxPreviewLoading] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // FX preview — re-runs 400ms after the user stops typing the amount.
  // Skipped on the bank tab (no FX there: ops settle in the local currency).
  useEffect(() => {
    if (method !== 'card') { setFxPreview(null); setFxPreviewError(null); return; }
    const num = parseFloat(amount);
    if (!num || num <= 0) { setFxPreview(null); setFxPreviewError(null); return; }
    setFxPreviewLoading(true);
    const t = setTimeout(() => {
      revolutApi.fxPreview(advertiserId, num)
        .then(p => {
          if (p.needs_conversion && p.charge_amount && p.charge_currency && p.fx_rate != null) {
            setFxPreview({
              charge_amount: p.charge_amount,
              charge_currency: p.charge_currency,
              fx_rate: p.fx_rate,
              fx_margin_pct: p.fx_margin_pct ?? 0,
            });
          } else {
            setFxPreview(null);
          }
          setFxPreviewError(null);
        })
        .catch(e => {
          setFxPreview(null);
          setFxPreviewError(e?.response?.data?.detail || null);
        })
        .finally(() => setFxPreviewLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [advertiserId, amount, method]);

  // Load saved cards once on mount; pre-select the default if any can be re-charged
  useEffect(() => {
    paymentMethodsApi.list(advertiserId).then(cards => {
      setSavedCards(cards);
      const usable = cards.filter(c =>
        c.status === 'active' && !((c.metadata as any)?.mit_supported === false)
      );
      const def = usable.find(c => c.is_default_for_topup) || usable[0];
      if (def) setSelectedCardId(def.id);
    }).catch(() => setSavedCards([]));
  }, [advertiserId]);

  const handlePayCard = async () => {
    setError(null);
    const num = parseFloat(amount);
    if (!num || num <= 0) { setError(t('billing.errValidAmount')); return; }
    setBusy(true);

    // Branch: pay with a saved card (no checkout redirect) vs new card (Revolut hosted)
    if (selectedCardId) {
      try {
        const resp = await revolutApi.chargeSaved({
          advertiser_id: advertiserId,
          payment_method_id: selectedCardId,
          amount: num,
          // Stays English: this is the statement descriptor the payment provider
          // puts on the cardholder's bank statement, not interface text.
          description: `Top-up ${num.toFixed(2)} ${currency}`,
          browser_data: collectBrowserData(),
        });
        if (resp.status === 'failed') {
          setError(resp.failure_reason || t('billing.errPaymentFailed'));
        } else {
          setSuccessMsg(
            resp.status === 'succeeded'
              ? t('billing.chargedOk', { amount: num.toFixed(2), ccy: currency })
              : t('billing.authorisedOk')
          );
          onCheckoutOpened?.();
          setTimeout(() => onClose(), 2000);
        }
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || t('billing.errChargeSavedCard'));
      } finally {
        setBusy(false);
      }
      return;
    }

    try {
      const returnUrl = `${window.location.origin}/billing?topup=complete`;
      const resp = await revolutApi.topup({
        advertiser_id: advertiserId, amount: num, return_url: returnUrl,
        save_payment_method: saveCard,
        // Stays English: this is the statement descriptor the payment provider
        // puts on the cardholder's bank statement, not interface text.
        description: `Top-up ${num.toFixed(2)} ${currency}`,
      });

      // Preferred: open Revolut Checkout in a native popup (no redirect).
      if (resp.revolut_public_id) {
        await openRevolutCheckout({
          token: resp.revolut_public_id,
          environment: resp.revolut_environment === 'sandbox' ? 'sandbox' : 'production',
          // Per Revolut docs the card is only tokenised when this is set on
          // the client-side widget. Backend save_payment_method_for is ignored.
          savePaymentMethodFor: saveCard ? 'merchant' : undefined,
          onSuccess: () => {
            setSuccessMsg(t('billing.paymentReceived'));
            // Schedule the BillingPage refresh AFTER the user actually paid
            // (otherwise the 6s timer ticks before the webhook arrives).
            onCheckoutOpened?.();
            setTimeout(() => onClose(), 2000);
          },
          onError: (msg) => { setError(msg); setBusy(false); },
          onCancel: () => { setBusy(false); },
        });
        return;
      }

      // Fallback: same-tab redirect when the gateway didn't expose a token.
      onCheckoutOpened?.();
      window.location.assign(resp.checkout_url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || t('billing.errStartPayment'));
      setBusy(false);
    }
  };

  const handleCreateBankRequest = async () => {
    setError(null);
    const num = parseFloat(amount);
    if (!num || num <= 0) { setError(t('billing.errValidAmount')); return; }
    setBusy(true);
    try {
      const resp = await bankTransferApi.createRequest({
        advertiser_id: advertiserId, amount: num,
      });
      setBankDetails(resp);
      onCheckoutOpened?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || t('billing.errCreateRequest'));
    } finally {
      setBusy(false);
    }
  };

  // ─── Bank-transfer details view (after request created) ─────────────────
  if (bankDetails) {
    const copy = (text: string) => navigator.clipboard.writeText(text);
    return (
      <div className="topup-overlay" onClick={onClose}>
        <div className="topup-dialog" onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 92vw)' }}>
          <div className="topup-header">
            <h3>{t('billing.bankInstructions')}</h3>
            <button className="topup-close" onClick={onClose} aria-label={t('common.close')}>×</button>
          </div>
          <div className="topup-body">
            <p style={{ margin: 0, fontSize: 13, color: '#6c757d' }}>
              {t('billing.bankIntro')}
            </p>

            <div className="bank-detail">
              <div className="bank-detail-label">{t('billing.beneficiary')}</div>
              <div className="bank-detail-value">{bankDetails.bank_details.company_name || '—'}</div>
            </div>
            {bankDetails.bank_details.legal_address && (
              <div className="bank-detail">
                <div className="bank-detail-label">{t('billing.address')}</div>
                <div className="bank-detail-value">{bankDetails.bank_details.legal_address}</div>
              </div>
            )}
            <div className="bank-detail">
              <div className="bank-detail-label">IBAN</div>
              <div className="bank-detail-value mono">
                {bankDetails.bank_details.bank_iban || '—'}
                <button className="bank-detail-copy" onClick={() => copy(bankDetails.bank_details.bank_iban || '')}>{t('clients.copy')}</button>
              </div>
            </div>
            {bankDetails.bank_details.bank_swift && (
              <div className="bank-detail">
                <div className="bank-detail-label">{t('billing.bicSwift')}</div>
                <div className="bank-detail-value mono">
                  {bankDetails.bank_details.bank_swift}
                  <button className="bank-detail-copy" onClick={() => copy(bankDetails.bank_details.bank_swift!)}>{t('clients.copy')}</button>
                </div>
              </div>
            )}
            <div className="bank-detail bank-detail-highlight">
              <div className="bank-detail-label">{t('billing.paymentReference')}</div>
              <div className="bank-detail-value mono">
                {bankDetails.payment_reference}
                <button className="bank-detail-copy" onClick={() => copy(bankDetails.payment_reference)}>{t('clients.copy')}</button>
              </div>
            </div>
            <div className="bank-detail">
              <div className="bank-detail-label">{t('billing.amount')}</div>
              <div className="bank-detail-value">
                <strong>{Number(bankDetails.amount).toFixed(2)} {bankDetails.currency}</strong>
              </div>
            </div>

            <p className="topup-hint">
              {t('billing.refWarning')}
            </p>

            <div className="topup-actions">
              <button className="topup-pay" onClick={onClose}>{t('billing.done')}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Method selector + amount form ──────────────────────────────────────
  return (
    <div className="topup-overlay" onClick={onClose}>
      <div className="topup-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="topup-header">
          <h3>{t('billing.topUpBalance')}</h3>
          <button className="topup-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        {(cardAvailable && bankAvailable) && (
          <div className="topup-method-tabs">
            <button
              className={`topup-method-tab ${method === 'card' ? 'active' : ''}`}
              onClick={() => setMethod('card')}
            >{t('billing.methodCard')}</button>
            <button
              className={`topup-method-tab ${method === 'bank' ? 'active' : ''}`}
              onClick={() => setMethod('bank')}
            >{t('billing.methodBank')}</button>
          </div>
        )}

        <div className="topup-body">
          <div className="topup-presets">
            {presetAmounts.map(p => (
              <button
                key={p}
                className={`topup-preset ${parseFloat(amount) === p ? 'active' : ''}`}
                onClick={() => setAmount(String(p))}
              >
                {p.toLocaleString()} {currency}
              </button>
            ))}
          </div>

          <label className="topup-amount-label">
            <span>{t('billing.amountWithCcy', { ccy: currency })}</span>
            <input
              type="number" min="1" step="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              autoFocus
            />
          </label>

          {method === 'card' && fxPreview && (
            <div className="topup-fx-preview">
              <div className="topup-fx-row">
                <span className="topup-fx-arrow">→</span>
                <span className="topup-fx-amount">
                  ≈ {fxPreview.charge_amount.toLocaleString(currentLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {fxPreview.charge_currency}
                </span>
                <span className="topup-fx-meta">
                  {t('billing.fxWillCharge')}
                </span>
              </div>
              <div className="topup-fx-rate">
                {t('billing.fxRate', { from: currency, rate: fxPreview.fx_rate.toFixed(6), to: fxPreview.charge_currency })}
                {fxPreview.fx_margin_pct > 0 && t('billing.fxMargin', { pct: fxPreview.fx_margin_pct })}
              </div>
            </div>
          )}
          {method === 'card' && fxPreviewLoading && !fxPreview && (
            <div className="topup-fx-preview loading">{t('billing.fxCalculating')}</div>
          )}
          {method === 'card' && fxPreviewError && (
            <div className="topup-fx-preview error">{t('billing.fxPrefix', { msg: fxPreviewError })}</div>
          )}

          {method === 'card' && savedCards && savedCards.length > 0 && (
            <div className="topup-cards">
              <div className="topup-cards-label">{t('billing.payWith')}</div>
              {savedCards.map(c => {
                const noMit = (c.metadata as any)?.mit_supported === false;
                const mismatch = !!c.profile_mismatch;
                const disabled = c.status !== 'active' || noMit || mismatch;
                return (
                  <label
                    key={c.id}
                    className={`topup-card-option ${selectedCardId === c.id ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                  >
                    <input
                      type="radio"
                      name="topup-card"
                      checked={selectedCardId === c.id}
                      disabled={disabled}
                      onChange={() => setSelectedCardId(c.id)}
                    />
                    <div className="topup-card-brand">{shortBrand(c.brand)}</div>
                    <div className="topup-card-text">
                      <div className="topup-card-title">
                        {`${readableBrand(c.brand)} •••• ${c.last4 || ''}`.trim()}
                      </div>
                      {noMit && <div className="topup-card-sub">{t('billing.cardSandbox')}</div>}
                      {mismatch && <div className="topup-card-sub">{t('billing.cardProfileChanged')}</div>}
                      {c.status === 'expired' && <div className="topup-card-sub">{t('billing.cardExpired')}</div>}
                    </div>
                  </label>
                );
              })}
              <label className={`topup-card-option ${selectedCardId === null ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="topup-card"
                  checked={selectedCardId === null}
                  onChange={() => setSelectedCardId(null)}
                />
                <div className="topup-card-brand new">+</div>
                <div className="topup-card-text">
                  <div className="topup-card-title">{t('billing.useNewCard')}</div>
                  <div className="topup-card-sub">{t('billing.opensCheckout')}</div>
                </div>
              </label>
            </div>
          )}

          {method === 'card' && selectedCardId === null && (
            <label className="topup-save-toggle">
              <input
                type="checkbox" checked={saveCard}
                onChange={(e) => setSaveCard(e.target.checked)}
              />
              <span>{t('billing.saveCardForAuto')}</span>
            </label>
          )}

          {error && <div className="topup-error">{error}</div>}
          {successMsg && <div className="topup-success">{successMsg}</div>}

          <div className="topup-actions">
            <button className="topup-cancel" onClick={onClose} disabled={busy}>{t('common.cancel')}</button>
            {method === 'card' ? (
              <button className="topup-pay" onClick={handlePayCard} disabled={busy}>
                {busy
                  ? t(selectedCardId ? 'billing.charging' : 'billing.openingCheckout')
                  : selectedCardId
                    ? t('billing.payAmountWithCard', {
                      amount: amount || '0', ccy: currency,
                      last4: savedCards?.find(c => c.id === selectedCardId)?.last4 || '',
                    })
                    : t('billing.payAmount', {
                      amount: amount ? Number(amount).toLocaleString(currentLocale()) : '0', ccy: currency,
                    })}
              </button>
            ) : (
              <button className="topup-pay" onClick={handleCreateBankRequest} disabled={busy}>
                {busy ? t('billing.generatingDots') : t('billing.generateRequestFor', {
                  amount: amount ? Number(amount).toLocaleString(currentLocale()) : '0', ccy: currency,
                })}
              </button>
            )}
          </div>

          <p className="topup-hint">
            {t(method === 'card'
              ? (selectedCardId ? 'billing.hintSavedCard' : 'billing.hintNewCard')
              : 'billing.hintBank')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopUpDialog;
